import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, dateFnsLocalizer, Views, type Event } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { pl } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useAuth } from "../auth/AuthProvider";
import { availabilityApi, teachersApi, type Slot, type TeacherProfile } from "../lib/lessons";

const locales = { pl };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

export function CalendarPage(): JSX.Element {
  const { user } = useAuth();
  const [profile, setProfile] = useState<TeacherProfile | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    (async () => {
      try {
        const { profile } = await teachersApi.myProfile();
        setProfile(profile);
        if (profile) {
          const { slots } = await availabilityApi.mine();
          setSlots(slots);
        }
      } catch (e) {
        setError(String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const events: Event[] = useMemo(
    () =>
      slots.map((s) => ({
        id: s.id,
        title: "Dostępność",
        start: new Date(s.starts_at),
        end: new Date(s.ends_at),
        resource: s,
      })),
    [slots],
  );

  async function becomeTeacher(): Promise<void> {
    const { profile } = await teachersApi.upsertProfile({
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    setProfile(profile);
  }

  async function addSlot(start: Date, end: Date): Promise<void> {
    try {
      const { slot } = await availabilityApi.create({
        starts_at: start.getTime(),
        ends_at: end.getTime(),
      });
      setSlots((s) => [...s, slot]);
    } catch (e) {
      alert(`Nie udało się dodać slotu: ${e}`);
    }
  }

  async function removeSlot(slotId: string): Promise<void> {
    if (!confirm("Usunąć slot?")) return;
    try {
      await availabilityApi.remove(slotId);
      setSlots((s) => s.filter((x) => x.id !== slotId));
    } catch (e) {
      alert(`Nie udało się usunąć: ${e}`);
    }
  }

  if (!user) return <Link to="/" className="text-white p-6 block">Zaloguj się</Link>;
  if (loading) return <div className="p-6 text-ink-400">Wczytywanie…</div>;
  if (error) return <div className="p-6 text-red-300">{error}</div>;

  if (!profile) {
    return (
      <div className="h-full bg-ink-900 text-white p-6">
        <Link to="/" className="text-xs text-ink-400 hover:text-white">← Wróć</Link>
        <h1 className="text-lg font-semibold mt-1 mb-6">Kalendarz nauczyciela</h1>
        <div className="bg-ink-800 border border-ink-700 rounded-2xl p-6 max-w-md">
          <p className="text-sm text-ink-300 mb-4">
            Aby zarządzać dostępnością i przyjmować rezerwacje, włącz konto nauczyciela.
          </p>
          <button
            onClick={becomeTeacher}
            className="bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold rounded-lg px-4 py-2 text-sm"
          >
            Zostań nauczycielem
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-ink-900 text-white">
      <header className="px-6 py-4 border-b border-ink-700">
        <Link to="/" className="text-xs text-ink-400 hover:text-white">← Wróć</Link>
        <h1 className="text-lg font-semibold mt-1">Moja dostępność</h1>
        <p className="text-xs text-ink-500">Zaznacz obszar w kalendarzu, żeby dodać slot. Kliknij istniejący slot, by usunąć.</p>
      </header>
      <div className="flex-1 bg-white text-ink-900 p-2">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          selectable
          defaultView={Views.WEEK}
          step={30}
          timeslots={2}
          min={new Date(1970, 0, 1, 7, 0)}
          max={new Date(1970, 0, 1, 22, 0)}
          onSelectSlot={(s) => addSlot(s.start as Date, s.end as Date)}
          onSelectEvent={(e) => removeSlot((e.resource as Slot).id)}
          culture="pl"
          style={{ height: "100%" }}
        />
      </div>
    </div>
  );
}
